import {apiService, getAuthValues, setAuthHeader} from "./api";
import {
    User,
    Request,
    RequestAPIResponse,
    Verification,
    Participant
} from "../models";

type RequestCreationProps = {
    description: string
    recipients: string[]
    resolver: string
    title: string
};
type IntentSuccess = {verificationID: string,expiresAt: string};
type Decision = {id: string, note?: string, proceed: boolean};
export type Maybe<T> =
| {type: "success", value: T}
| {type: "error", message: string};

function deduplicate(val: User[]): User[] {
    const map = val.reduce(function (acc, user) {
        if (!acc[user.email]) {
            acc[user.email] = user;
        }
        return acc;
    }, Object.create(null));
    return Object.values(map);
}

async function searchUsers(query: string): Promise<User[]> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    res = await apiService.get<{results: User[]}>(
        `/company?type=searchUser&searchQuery=${query}`
    );
    return deduplicate(res.data.results);
}

async function fetchUsers(): Promise<User[]> {
    const {companyId, token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    res = await apiService.get<User[]>(
        `/company?type=getCompanyUsers&companyId=${companyId}`
    );
    return res.data;
}

async function fetchParticipants(id: string): Promise<Maybe<Participant[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.get<{participants: Participant[]}>(
            `/request/participants?id=${id}`
        );
        return {type: "success", value: res.data.participants};
    } catch (e: any) {
        res = e.response?.data?.message ?? "Failed to fetch participants";
        return {message: res, type: "error"};
    }
}

async function fetchRequests(): Promise<Maybe<Request[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.get<{requests: RequestAPIResponse[]}>(
            "/request/list"
        );
        return {
            type: "success",
            value: res.data.requests.map(RequestAPIResponse.toRequest)
        };
    } catch (err: any) {
        res = err.response?.data?.message ?? "Failed to retrieve requests";
        return {message: res, type: "error"};
    }
}

async function create(data: RequestCreationProps): Promise<Maybe<IntentSuccess>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<{intent: IntentSuccess}>(
            "/request/",
            data
        );
        return {type: "success", value: res.data.intent};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            message: res ?? "Failed to initiate request creation",
            type: "error"
        };
    }
}

async function cancel(id: string): Promise<Maybe<IntentSuccess>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<{intent: IntentSuccess}>(
            "/request/cancel",
            {id}
        );
        return {type: "success", value: res.data.intent};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            message: res ?? "Failed to initiate request cancellation",
            type: "error"
        };
    }
}

async function assignResolver(id: string, email: string): Promise<Maybe<IntentSuccess>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<{intent: IntentSuccess}>(
            "/request/reassign",
            {email, id}
        );
        return {type: "success", value: res.data.intent};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            message: res ?? "Failed to initiate resolver assignment",
            type: "error"
        };
    }
}

async function resolve(p: Decision): Promise<Maybe<IntentSuccess>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<{intent: IntentSuccess}>(
            "/request/resolve",
            p
        );
        return {type: "success", value: res.data.intent};
    } catch (err: any) {
        res = err.response?.data?.message;
        return {
            message: res ?? "Failed to initiate request resolution",
            type: "error"
        };
    }
}

async function getIntents(): Promise<Maybe<Verification[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.get<{results: Verification[]}>("/request/intents");
        return  {type: "success", value: res.data.results};
    } catch (err: any) {
        res = err.response.data.message;
        return {message: res ?? "Failed to fetch pending intents", type: "error"}
    }
}

export {
    assignResolver,
    cancel,
    create,
    fetchParticipants,
    fetchRequests,
    fetchUsers,
    getIntents,
    resolve,
    searchUsers
};
